import { useState, useEffect, useRef } from "react";
import {
	ResponsiveContainer,
	Tooltip,
	LineChart,
	XAxis,
	YAxis,
	Label,
	ReferenceArea,
	Line,
} from "recharts";
import { numberFormatter } from "../../../utils";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { CSVLink } from "react-csv";
import TableTitle from "../../../components/common/Table/TableTitle";

const SKUPareto = (props: any) => {
	const chartData = props.data.skuPareto[0].coordinates;
	const { exportData } = props;
	const exportFullRef = useRef<any>(null);

	const exportExcel = () => {
		props.getSkuExportData();
	};

	useEffect(() => {
		if (exportData?.length !== 0) {
			exportFullRef?.current?.link.click();
			props?.clearExport();
		}
	}, [exportData]);

	const ref = props.refArea;
	const tableData = props.data.skuChart?.reduce(
		(acc: any, current: any) => {
			if (current?.paretoFlag) {
				acc[current?.paretoFlag]["0-14"] = current["#0-14"];
				acc[current?.paretoFlag]["14-UL"] = current["#14-UL"];
			}

			acc.total["0-14"] += current["#0-14"];
			acc.total["14-UL"] += current["#14-UL"];
			acc.ul = current["#UL"];
			return acc;
		},
		{
			"not loss leader": { "0-14": 0, "14-UL": 0 },
			"loss leaders": { "0-14": 0, "14-UL": 0 },
			"uniquely purchased": { "0-14": 0, "14-UL": 0 },
			total: { "0-14": 0, "14-UL": 0 },
			ul: 0,
		}
	);

	const csvHeaders = [
		{ label: "L1", key: "l1" },
		{ label: "L2", key: "l2" },
		{ label: "L3", key: "l3" },
		{ label: "L4", key: "l4" },
		{ label: "Part Number", key: "MAN_PART_NO" },
		{ label: "Material Number", key: "MATERIAL_MATERIAL" },
		{ label: "Spend ($)", key: "cost" },
		{ label: "Gross Margin ($)", key: "GM$" },
		{ label: "SKU GM%", key: "SKU_GM%" },
		{ label: "SKU category", key: "paretoFlag" },
	];

	const customTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;

			const popupStyle = {
				width: "fit-content",
				background: "white",
				border: "1px solid #ccc",
				boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
				borderRadius: "1vh",
				cursor: "pointer",
				color: "black",
			};

			return (
				<div style={popupStyle}>
					{/* Header Row */}
					<div className="w-full flex justify-start h-[3vh] items-center bg-[#f0f4f8] py-[3vh] px-[1vh] textSmall">
						<div className="font-bold uppercase">
							Cumulative Margin:
						</div>
						<div
							className="font-bold ml-1"
							style={{ color: "#3f3f3f" }}
						>
							{numberFormatter(data.cumilativeMargin, 0, "$")}
						</div>
					</div>
					{/* Data Row */}
					<div className="h-fit p-[1vh] mt-[.5vh] mb-[.5vh]">
						<p
							className="label"
							style={{ padding: "0", margin: "0" }}
						>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								# SKUs:{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{data.itemsNo}
							</label>
						</p>
					</div>
				</div>
			);
		}
		return null;
	};

	const renderContent = (props: any) => {
		const { x, y, width, value, name } = props.viewBox;
		let totalCount = chartData[chartData.length - 1].itemsNo;
		let percent = Math.round((ref.itemsNo / totalCount) * 100);
		const lines = [
			"80% of the margin ",
			`comes from ~${percent}%`,
			`SKUs (${ref.itemsNo})`,
		];
		return (
			<text
				fill="#9acd32b8"
				x={x}
				y={y}
				fontSize={14}
				fontStyle={"italic"}
			>
				{lines.map((line: any, index: any) => (
					<tspan key={index} x={1.5 * x} dy={index === 0 ? 30 : 20}>
						{line}
					</tspan>
				))}
			</text>
		);
	};

	return (
		<div className="h-full w-full flex flex-col px-2">
			<div className=" w-full flex flex-col">
				<div className="headSpace">
					<div className="headContent">
						# of SKUs by Cumulative Margin (Sorted by Margin %)
					</div>

					<FileDownloadIcon
						className="mr-[2vh] cursor-pointer"
						style={{
							background: "#9acd32b8",
							borderRadius: ".3vh",
						}}
						onClick={() => {
							exportExcel();
						}}
					/>
					<CSVLink
						ref={exportFullRef}
						data={exportData}
						headers={csvHeaders}
						filename="skuPareto.csv"
						className="hidden"
						target="_blank"
					>
						{" "}
					</CSVLink>
				</div>
			</div>
			<div className="grow w-full h-[45%]">
				<ResponsiveContainer width="100%" height="100%">
					<LineChart
						data={chartData}
						margin={{ left: 20, right: 20 }}
					>
						<XAxis
							className="graph-label"
							dataKey="itemsNo"
							type="number"
							domain={[0, "dataMax"]}
						/>
						<YAxis
							className="graph-label"
							tickFormatter={(value: number) =>
								numberFormatter(value, 0)
							}
						>
							<Label
								className="graph-label"
								value="Margin ($)"
								angle={-90}
								position="insideLeft"
								style={{ fill: "#000", textAnchor: "middle" }}
							/>
						</YAxis>
						<Tooltip
							contentStyle={{
								color: "white",
								backgroundColor: "rgb(26, 25, 22)",
							}}
							cursor={{ strokeDasharray: "3 3", fill: "#212121" }}
							content={customTooltip}
						/>
						<ReferenceArea
							x1={0}
							x2={ref.itemsNo}
							y1={0}
							y2={ref.cumilativeMargin}
							stroke="#e3e48d"
							strokeOpacity={0.4}
							fill="#e3e48d"
							fillOpacity={0.4}
						>
							<Label
								content={(obj: any) => renderContent(obj)}
								stroke="white"
								position="right"
								style={{ fill: "white" }}
							/>
						</ReferenceArea>
						<Line
							dataKey="cumilativeMargin"
							stroke="#9acd32b8"
							dot={false}
							strokeWidth={3}
						/>
					</LineChart>
				</ResponsiveContainer>
			</div>
			<div className="grow w-full h-[50%]">
				<table className="common-table-ui">
					<thead>
						<tr>
							<th rowSpan={2} className="w-[60%]">
								Low margin SKU category
							</th>
							<th colSpan={2} className="paretoTh w-[45%]">
								# SKUs
							</th>
						</tr>
						<tr>
							<th className="paretoTh">Gross Margin (0 - 14%)</th>
							<th className="paretoTh">{`Gross Margin (14 - ${(
								tableData.ul * 100
							).toFixed(0)}%)`}</th>
						</tr>
					</thead>
					<tbody>
						<tr className="paretoTr">
							<td>Loss Leader</td>
							<td>{tableData["loss leaders"]["0-14"]}</td>
							<td>{tableData["loss leaders"]["14-UL"]}</td>
						</tr>
						<tr className="paretoTr">
							<td>Not Loss Leader</td>
							<td>{tableData["not loss leader"]["0-14"]}</td>
							<td>{tableData["not loss leader"]["14-UL"]}</td>
						</tr>
						<tr className="paretoTr">
							<td>Purchased Individually</td>
							<td>{tableData["uniquely purchased"]["0-14"]}</td>
							<td>{tableData["uniquely purchased"]["14-UL"]}</td>
						</tr>
						<tr className="font-medium paretoTr">
							<td>Total</td>
							<td>{tableData?.total["0-14"]}</td>
							<td>{tableData?.total["14-UL"]}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default SKUPareto;
